import JSZip from "jszip";
import { isLockedItem, type CanvasItem, type Space } from "@bedrock/domain";
import type { LocalStore } from "@bedrock/local-store";

export async function exportLibrary(store: LocalStore): Promise<Blob> {
  const zip = new JSZip();
  const spaces = await store.listSpaces({ includeDeleted: false });
  const manifest: { version: 1; exportedAt: string; spaces: Space[]; items: CanvasItem[] } = { version: 1, exportedAt: new Date().toISOString(), spaces, items: [] };
  for (const space of spaces) {
    const items = await store.listItems(space.id, { includeDeleted: false });
    manifest.items.push(...items);
    const folder = zip.folder(`spaces/${safeName(space.title)}-${space.id.slice(-8)}`);
    for (const item of items) {
      // Locked bodies stay encrypted in the manifest; there is no readable Markdown to write.
      if (isLockedItem(item)) continue;
      if (item.props.kind === "sticky-note") folder?.file(`${safeName(item.props.plainText.slice(0, 42) || "note")}-${item.id.slice(-6)}.md`, item.props.markdown);
      if (item.props.kind === "document") folder?.file(`${safeName(item.props.title)}-${item.id.slice(-6)}.md`, item.props.markdown);
      if (item.props.kind === "web-clip") folder?.file(`clips/${safeName(item.props.title)}.json`, JSON.stringify(item.props, null, 2));
      if (item.props.kind === "date-event") folder?.file(`events/${safeName(item.props.title)}-${item.id.slice(-6)}.json`, JSON.stringify(item.props, null, 2));
      if (item.props.kind === "image" || item.props.kind === "video" || item.props.kind === "pdf" || item.props.kind === "spreadsheet") {
        const cached = await store.getCachedAsset(item.props.assetId);
        if (cached) folder?.file(`assets/${safeAssetFilename(item.props.filename)}`, cached.blob);
      }
    }
  }
  zip.file("manifest.json", JSON.stringify(manifest, null, 2));
  return zip.generateAsync({ type: "blob", compression: "DEFLATE", compressionOptions: { level: 6 } });
}

function safeName(value: string): string {
  return value.normalize("NFKD").replace(/[^a-zA-Z0-9-_ ]/g, "").trim().replace(/\s+/g, "-").toLocaleLowerCase().slice(0, 80) || "untitled";
}

function safeAssetFilename(value: string): string {
  const extension = /\.([a-z0-9]{1,12})$/i.exec(value)?.[1]?.toLocaleLowerCase();
  const basename = extension ? value.slice(0, -(extension.length + 1)) : value;
  return `${safeName(basename)}${extension ? `.${extension}` : ""}`;
}
